import React, {useState} from "react";
import {updateBookingStatus} from "../../api/api";
import {toast} from "../../hooks/use-toast";
import {Calendar, CheckCircle, Clock, Info, Users, X} from "lucide-react";
import {Badge} from "../ui/badge";
import {Button} from "../ui/button";

// Types
type Restaurant = {
    name: string;
    image?: string;
    cuisine?: string;
    location?: string;
    rating?: number;
};

export type Booking = {
    id: number;
    code_to_verify?: string | number;
    date: string;
    time: string;
    guests: number;
    total_price: number;
    dietary?: string | null;
    notes?: string | null;
    status: string;
    restaurant?: Restaurant;
};

const CheckBookingVerificationModal: React.FC<{
    open: boolean;
    booking: Booking | null;
    onClose: () => void;
}> = ({
          open,
          booking,
          onClose,
      }) => {
    const [verifying, setVerifying] = useState(false);
    const [verified, setVerified] = useState(false);

    if (!open || !booking) return null;

    const handleVerify = async () => {
        if (!booking?.id) return;
        setVerifying(true);
        try {
            await updateBookingStatus(booking.id, "completed");
            setVerified(true);
            setTimeout(() => {
                setVerified(false);
                setVerifying(false);
                onClose();
            }, 1200);
        } catch (error) {
            setVerifying(false);
            toast({title: "Error", description: "Verification failed."});
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4 backdrop-blur-sm">
            <div
                className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-lg p-0 overflow-hidden">
                {/* Header Image */}
                {booking.restaurant?.image && (
                    <div className="w-full h-44 bg-gray-100 dark:bg-gray-800 relative">
                        <img
                            src={booking.restaurant.image}
                            alt={booking.restaurant.name}
                            className="object-cover w-full h-full"
                            draggable={false}
                        />
                        <button
                            onClick={onClose}
                            className="absolute top-4 right-4 text-white bg-black/40 hover:bg-red-500 hover:text-white rounded-full p-1 transition"
                            aria-label="Close"
                        >
                            <X className="h-6 w-6"/>
                        </button>
                        <div className="absolute bottom-0 left-0 bg-black/60 px-4 py-2 w-full">
                            <div className="flex items-center gap-3">
                                <span className="text-lg font-bold text-white">{booking.restaurant.name}</span>
                                <Badge className="bg-primary">{booking.restaurant.cuisine || "Cuisine"}</Badge>
                                <span className="text-xs text-gray-200">{booking.restaurant.location}</span>
                            </div>
                        </div>
                    </div>
                )}

                {/* Modal Content */}
                <div className="p-6">
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                            <Calendar className="h-5 w-5 text-primary"/>
                            <span className="font-semibold">{booking.date}</span>
                            <Clock className="h-5 w-5 text-primary"/>
                            <span className="font-semibold">{booking.time}</span>
                        </div>
                        <Badge
                            className={
                                booking.status === "confirmed"
                                    ? "bg-green-500"
                                    : booking.status === "completed"
                                        ? "bg-blue-500"
                                        : "bg-red-500"
                            }
                        >
                            {booking.status[0].toUpperCase() + booking.status.slice(1)}
                        </Badge>
                    </div>

                    <div className="flex items-center gap-6 mb-3">
                        <div className="flex items-center gap-1 text-gray-700 dark:text-gray-200">
                            <Users className="h-4 w-4"/>
                            <span className="font-medium">{booking.guests}</span>
                            <span className="text-sm text-gray-500 ml-1">Guests</span>
                        </div>
                        <div className="text-gray-500 dark:text-gray-400 text-sm">
                            Total: <span className="font-semibold text-primary">{booking.total_price} €</span>
                        </div>
                    </div>

                    {(booking.notes || booking.dietary) && (
                        <div className="mb-4 flex flex-col gap-1">
                            {booking.notes && (
                                <div className="flex items-center text-gray-700 dark:text-gray-200 text-sm">
                                    <Info className="h-4 w-4 mr-1"/>
                                    <span>Notes: <span className="font-semibold">{booking.notes}</span></span>
                                </div>
                            )}
                            {booking.dietary && (
                                <div className="flex items-center text-gray-700 dark:text-gray-200 text-sm">
                                    <Info className="h-4 w-4 mr-1"/>
                                    <span>Dietary: <span className="font-semibold">{booking.dietary}</span></span>
                                </div>
                            )}
                        </div>
                    )}

                    <div className="mb-6 flex flex-col items-center justify-center gap-2">
    <span className="uppercase text-xs font-semibold text-green-600 tracking-widest">
        Customer Verification Code
    </span>
                        <span
                            className="text-4xl font-mono font-bold tracking-widest text-green-600 bg-white dark:bg-gray-900 rounded-lg px-6 py-3 shadow-inner border border-green-500/40">
        {booking.code_to_verify ?? <span className="text-gray-400">No code</span>}
    </span>
                    </div>


                    {verified && (
                        <div className="flex items-center justify-center mb-3">
                            <CheckCircle className="text-green-600 h-6 w-6 mr-2"/>
                            <span className="font-semibold text-green-700">Booking verified!</span>
                        </div>
                    )}

                    <div className="flex justify-end gap-2 mt-2">
                        <Button variant="outline" onClick={onClose} disabled={verifying}>
                            Close
                        </Button>
                        <Button
                            className="bg-green-600 hover:bg-green-700 text-white font-semibold"
                            disabled={verifying || verified || !booking.code_to_verify}
                            onClick={handleVerify}
                        >
                            {verifying ? "Verifying…" : verified ? "Verified" : "Verify Booking"}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CheckBookingVerificationModal;